import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTableData } from "../../hooks/useTableData";
import { getCoachAICallHistory } from "../../apis/callLogs";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import Input from "../Tags/Input";
import Image from "../Tags/Image";
import { Badge } from "../ui/badge";
import TableLoader from "../Loaders/TableLoader";
import NoData from "../NoData";
import Pagination from "../Pagination";
import { returnArray } from "../../utils/GenericFn";
import SharedDataContext from "../../SharedDataContext";
import Filter from "../Filter";
import { ArrowUp, ArrowDown } from "lucide-react";
import { isEmptyObject } from "../../utils/GenericFn";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";

export default function CallHistoryTable({ onFilterReady }) {
  const navigate = useNavigate();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [callHistoryData, setCallHistoryData] = useState({
    data: [],
    filterMetadata: {},
    columnMetadata: {},
    pagination: {},
  });
  const captureEvent = useCaptureEvent();

  const {
    data: callHistory,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    isLoading,
    isError,
    filters,
    sort,
    pagination,
    getPageNumber,
    handleSetSort,
    getFiltersObject,
  } = useTableData({
    queryKey: "get_coach_ai_call_history",
    fetchFn: getCoachAICallHistory,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      setCallHistoryData({
        data: data?.data || [],
        filterMetadata: data?.filterMetadata || {},
        columnMetadata: data?.columnMetadata || {},
        pagination: data?.pagination || {},
      });
    },
  });

  useEffect(() => {
    if (onFilterReady && callHistoryData?.filterMetadata && !isEmptyObject(callHistoryData.filterMetadata)) {
      onFilterReady({
        filterMetadata: callHistoryData.filterMetadata,
        getFiltersObject: getFiltersObject,
      });
    }
  }, [callHistoryData?.filterMetadata, getFiltersObject, onFilterReady]);

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  const getCallTypeBadge = (callType) => {
    if (callType === "cold") {
      return (
        <Badge
          radius="sm"
          variant="white"
          className="border-border bg-white text-text-600 text-sm font-medium dark:bg-[#1F242F] dark:border-gray-dark-200 dark:text-gray-dark-700"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_13070_33319)">
              <path
                d="M9.02918 4.25L2.96701 7.75M9.02918 4.25L9.57822 2.20096M9.02918 4.25L11.0782 4.79904M2.96701 7.75L0.917969 7.20096M2.96701 7.75L2.41797 9.79904M9.02914 7.74993L2.96696 4.24993M9.02914 7.74993L11.0782 7.2009M9.02914 7.74993L9.57823 9.79898M2.96696 4.24993L2.41804 2.20102M2.96696 4.24993L0.91804 4.79909M5.9981 2.5L5.9981 9.5M5.9981 2.5L4.4981 1M5.9981 2.5L7.4981 1M5.9981 9.5L4.4981 11M5.9981 9.5L7.4981 11"
                stroke="#535862"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_33319">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Cold call
        </Badge>
      );
    } else if (callType === "discovery") {
      return (
        <Badge
          radius="sm"
          variant="gray"
          className="border-border bg-white text-text-600 dark:bg-[#1F242F] dark:border-gray-dark-200 dark:text-gray-dark-700"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_13070_32519)">
              <path
                d="M10.5 10.5L8.325 8.325M9.5 5.5C9.5 7.70914 7.70914 9.5 5.5 9.5C3.29086 9.5 1.5 7.70914 1.5 5.5C1.5 3.29086 3.29086 1.5 5.5 1.5C7.70914 1.5 9.5 3.29086 9.5 5.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_32519">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Discovery call
        </Badge>
      );
    }
    return (
      <Badge
        radius="sm"
        variant="gray"
        className="border-border bg-white text-text-600 dark:bg-[#1F242F] dark:border-gray-dark-200 dark:text-gray-dark-700"
      >
        {callType || "-"}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 50) {
      return {
        bg: "bg-[#DCFAE6]",
        text: "text-[#079455]",
        border: "border-[#DCFAE6]",
      };
    } else if (score >= 30) {
      return {
        bg: "bg-[#FEF0C7]",
        text: "text-[#DC6803]",
        border: "border-[#FEF0C7]",
      };
    } else {
      return {
        bg: "bg-[#FEE4E2]",
        text: "text-[#D92D20]",
        border: "border-[#FEE4E2]",
      };
    }
  };

  const getBotAvatar = (bot) => {
    const avatar = bot?.bot_avatar || bot?.botAvatar || bot?.avatar;
    if (avatar) {
      return `/images/bots/${avatar}`;
    }
    return "/images/bots/avatar_7.svg";
  };

  const handleSelectAll = () => {
    if (selectedRows.length === callHistoryData.data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(callHistoryData.data.map((item) => item.id));
    }
  };

  const handleSelectRow = (isChecked, id) => {
    setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)));
  };

  const getUserName = (item) => {
    return item?.user_name || item?.userName || "-";
  };

  // Handle search as a filter
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value.trim()) {
      getFiltersObject({
        bot_name: {
          selectedScope: "bot_name",
          value: value.trim(),
        },
      });
      captureEvent("CAI_History_FilterChange", { key: "bot_name", value: value.trim() });
    } else {
      getFiltersObject({});
      captureEvent("CAI_History_FilterChange", { key: "bot_name", value: "" });
    }
  };

  return (
    <div className="w-full">
      {callHistoryData?.data && callHistoryData.data.length > 0 && !isLoading && (
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-background">
                    <tr>
                      <th scope="col" role="checkbox" className="h-4 w-4 !pr-0 !pl-4 !py-4">
                        {/* <Checkbox
                          className="h-4 w-4"
                          aria-checked={
                            selectedRows.length === callHistoryData.data.length &&
                            callHistoryData.data.length > 0
                          }
                          indeterminate={
                            selectedRows.length > 0 && selectedRows.length < callHistoryData.data.length
                          }
                          checked={
                            selectedRows.length === callHistoryData.data.length &&
                            callHistoryData.data.length > 0
                          }
                          onCheckedChange={handleSelectAll}
                        /> */}
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400 cursor-pointer"
                        onClick={() => handleSetSort("bot_name")}
                      >
                        <span className="inline-flex items-center">
                          Bot name
                          {/* {sort === `-bot_name` ? (
                            <ArrowDown className="inline-block ml-1 h-3 w-3" />
                          ) : sort === `bot_name` ? (
                            <ArrowUp className="inline-block ml-1 h-3 w-3" />
                          ) : (
                            <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                          )} */}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400"
                      >
                        User
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400 cursor-pointer"
                        onClick={() => {
                          handleSetSort("session_duration");
                          captureEvent("CAI_History_SortChange", { key: "session_duration" });
                        }}
                      >
                        <span className="inline-flex items-center">
                          Session duration
                          {sort === `-session_duration` ? (
                            <ArrowDown className="inline-block ml-1 h-3 w-3" />
                          ) : sort === `session_duration` ? (
                            <ArrowUp className="inline-block ml-1 h-3 w-3" />
                          ) : (
                            <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400"
                      >
                        Call type
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400 cursor-pointer"
                        onClick={() => {
                          handleSetSort("session_score");
                          captureEvent("CAI_History_SortChange", { key: "session_score" });
                        }}
                      >
                        <span className="inline-flex items-center">
                          Session score
                          {sort === `-session_score` ? (
                            <ArrowDown className="inline-block ml-1 h-3 w-3" />
                          ) : sort === `session_score` ? (
                            <ArrowUp className="inline-block ml-1 h-3 w-3" />
                          ) : (
                            <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="!px-3 !py-4 text-left text-sm font-semibold text-text-500 dark:text-gray-dark-400 cursor-pointer"
                        onClick={() => {
                          handleSetSort("call_date");
                          captureEvent("CAI_History_SortChange", { key: "call_date" });
                        }}
                      >
                        <span className="inline-flex items-center">
                          Date & time
                          {sort === `-call_date` ? (
                            <ArrowDown className="inline-block ml-1 h-3 w-3" />
                          ) : sort === `call_date` ? (
                            <ArrowUp className="inline-block ml-1 h-3 w-3" />
                          ) : (
                            <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                          )}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {returnArray(callHistoryData.data).map((item, index) => {
                      const sessionScore = item?.session_score ?? item?.sessionScore ?? 0;
                      const sessionDuration = item?.session_duration ?? item?.duration;
                      const callType = item?.call_type ?? item?.callType;
                      const callDate = item?.call_date ?? item?.callDate;
                      const botName = item?.bot_name ?? item?.botName;
                      const botTitle = item?.bot_title ?? item?.botTitle;
                      const botCompany = item?.bot_company ?? item?.botCompany;
                      const scoreColors = getScoreColor(sessionScore);
                      return (
                        <tr
                          key={item.id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-dark-50 cursor-pointer ${index % 2 !== 0 ? "" : ""}`}
                          onClick={() => {
                            if (item.session_id) {
                              navigate(`/${activeWorkspace?.slug}/coach-ai/session/${item.session_id}`, {
                                state: { historyRow: item },
                              });
                              captureEvent("CAI_History_RowClick", { session_id: item.session_id });
                            }
                          }}
                        >
                          <td
                            role="checkbox"
                            className="h-4 w-4 !pr-0 !pl-4 !py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* <Checkbox
                              className="h-4 w-4"
                              checked={selectedRows.includes(item.id)}
                              onCheckedChange={(isChecked) => handleSelectRow(isChecked, item.id)}
                            /> */}
                          </td>
                          <td className="!px-3 !py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <Image
                                src={getBotAvatar(item)}
                                width={40}
                                height={40}
                                className="rounded-full w-10 h-10 no-invert"
                              />
                              <div>
                                <div className="text-sm font-semibold text-text-900 dark:text-gray-dark-900">
                                  {botName || "-"}
                                </div>
                                <div className="text-sm text-text-600 dark:text-gray-dark-500">
                                  {botTitle || ""} {botCompany ? `@ ${botCompany}` : ""}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="!px-3 !py-4 whitespace-nowrap text-sm text-text-600 dark:text-gray-dark-500">
                            {getUserName(item)}
                          </td>
                          <td className="!px-3 !py-4 whitespace-nowrap text-sm text-text-600 dark:text-gray-dark-500">
                            {formatDuration(sessionDuration)}
                          </td>
                          <td className="!px-3 !py-4 whitespace-nowrap">{getCallTypeBadge(callType)}</td>
                          <td className="!px-3 !py-4 whitespace-nowrap">
                            <div
                              className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${scoreColors.bg} ${scoreColors.text} ${scoreColors.border} border-2 font-semibold text-sm`}
                            >
                              {sessionScore}
                            </div>
                          </td>
                          <td className="!px-3 !py-4 whitespace-nowrap text-sm text-text-600 dark:text-gray-dark-500">
                            {formatDateTime(callDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading && <TableLoader marginTop="mt-4" />}

      {!isLoading && callHistoryData?.data && callHistoryData.data.length === 0 && (
        <div className="mt-6">
          <NoData title="Error loading call history" description="Please try again or contact support" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="mt-6">
          <NoData title="Error loading call history" description="Please try again or contact support" />
        </div>
      )}

      {callHistoryData.pagination && callHistoryData.pagination.total > 0 && (
        <div className="flex items-center justify-between rounded-[--radius] py-3 mt-4">
          <div className="flex-1">
            <Pagination
              data={{
                currentPage: callHistoryData.pagination.current_page || callHistoryData.pagination.page || 1,
                lastPage: callHistoryData.pagination.last_page || 1,
                perPage: callHistoryData.pagination.per_page || 10,
                total: callHistoryData.pagination.total || 0,
              }}
              getPageNumber={(page) => {
                captureEvent("CAI_History_PageChange", { page });
                getPageNumber(page);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
